import { Injectable } from '@angular/core';
import { removeDateLag } from '../utilities/leg-attribute-utilities';
import { LegHelperService } from './leg-helper.service';
import { LegDateService } from './leg-date.service';
import { payLoadDate } from '../utilities/leg.utilities';
import { DateUtilities } from '@approot/utilities';
import { LegScheduleService } from './leg-schedule.service';

@Injectable({
  providedIn: 'root'
})
export class LegDateAttributeService {
  constructor(
    private legHelperService: LegHelperService,
    private legDateService: LegDateService,
    private legScheduleService: LegScheduleService
  ) {}

  public onWindowDateChange(): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    removeDateLag('windowDate', leg); // attempt to remove (1m) if its there
    this.legDateService.calculateExpirationSettlementDate(
      {
        formControl: form.get('windowDate'),
        setFormControl: true,
        controlName: 'windowDate',
        useDate: true
      },
      {
        formControl: null,
        setFormControl: false,
        controlName: ''
      },
      form.get('cross').value
    ); // set the windowDate and don't set the settlement date
  }

  public onWindowStartDateChange(): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    removeDateLag('windowStartDate', leg); // attempt to remove (1m) if its there
    this.legDateService.calculateExpirationSettlementDate(
      {
        formControl: form.get('windowStartDate'),
        setFormControl: true,
        controlName: 'windowStartDate',
        useDate: true
      },
      {
        formControl: null,
        setFormControl: false,
        controlName: ''
      },
      form.get('cross').value
    ); // set the windowStartDate and don't set the settlement date
  }

  public onWindowEndDateChange(): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    removeDateLag('windowEndDate', leg); // attempt to remove (1m) if its there
    this.legDateService.calculateExpirationSettlementDate(
      {
        formControl: form.get('windowEndDate'),
        setFormControl: true,
        controlName: 'windowEndDate',
        useDate: true
      },
      {
        formControl: null,
        setFormControl: false,
        controlName: ''
      },
      form.get('cross').value
    ); // set the windowEndDate and don't set the settlement date
  }

  public onStrikeFixingDate(): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    removeDateLag('strikeFixingDate', leg); // attempt to remove (1m) if its there
    this.legDateService.calculateExpirationSettlementDate(
      {
        formControl: form.get('strikeFixingDate'),
        setFormControl: true,
        controlName: 'strikeFixingDate',
        useDate: true
      },
      {
        formControl: null,
        setFormControl: false,
        controlName: ''
      },
      form.get('cross').value
    ); // set the windowEndDate and don't set the settlement date
  }

  public onStartDateChange(date: string): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    removeDateLag('startDate', leg); // attempt to remove (1m) if its there
    if (leg.productType === 'newdcd') {
      // disable dates
      form.get('redemptionDate').setValue('');
      form.get('expiryDate').setValue('');
      form.get('redemptionDate').disable();
      form.get('expiryDate').disable();
    }
    if (date) {
      if (leg.productType === 'newdcd') {
        // in newdcd mode get the next Business day
        const payload = {
          dateOrLag: payLoadDate(date),
          currencyOrCalendar: form.get('depositCurrency').value
        }; // we need to get the firstDate to be the next business day of that date
        this.legDateService.nextBusinessDay(payload, 'startDate', false, true); // calc the next businessDay
      } else {
        DateUtilities.onDateChanged(form.get('startDate'));
      }
    }
  }

  public onSwapStartDateChange(): void {
    const form = this.legHelperService.form;
    DateUtilities.onDateChanged(form.get('swapStartDate'));
    this.legScheduleService.rateIndexDateAdd({
      startDate: null,
      tenor: 'swapStartDate'
    });
  }

  public onSwapEndDateChange(): void {
    const form = this.legHelperService.form;
    DateUtilities.onDateChanged(form.get('swapEndDate'));
    this.legScheduleService.rateIndexDateAdd({
      startDate: 'swapStartDate',
      tenor: 'swapEndDate'
    });
  }

  public onSwaptionExpiryDateChange(): void {
    const form = this.legHelperService.form;
    DateUtilities.onDateChanged(form.get('swaptionExpiryDate'));
    this.legScheduleService.rateIndexDateAdd({
      startDate: null,
      tenor: 'swaptionExpiryDate'
    });
  }

  public onSwaptionSettlementDateChange(): void {
    const form = this.legHelperService.form;
    DateUtilities.onDateChanged(form.get('swaptionSettlementDate'));
    if (form.get('swaptionSettlementDate').valid) {
      this.legScheduleService.rateIndexDateAdd({
        startDate: null,
        tenor: 'swaptionSettlementDate'
      });
    }
  }

  public onFirstExerciseDateChange(): void {
    const form = this.legHelperService.form;
    DateUtilities.onDateChanged(form.get('firstExerciseDate'));
    if (this.legHelperService.presetsLoaded) {
      this.legScheduleService.rateIndexDateAdd({
        startDate: null,
        tenor: 'firstExerciseDate'
      });
    }
  }

  public onSettlementDateChange(): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    removeDateLag('settlementDate', leg); // attempt to remove (1m) if its there
    this.legDateService.copySettlementDateToPremiumDate();
    if (form.get('settlementDate').valid) {
      DateUtilities.onDateChanged(form.get('settlementDate'));
      switch (leg.productType) {
        case 'cashflow':
          this.legDateService.calculateExpirationSettlementDateCurrencyOnly(
            null,
            {
              formControl: form.get('settlementDate'),
              setFormControl: true,
              controlName: 'settlementDate',
              useDate: true
            }
          ); // we want to only set the settlementDate
          break;

        case 'europeanbasketoption':
          if (form.get('basket')) {
            this.legDateService.calculateExpirationSettlementDate(
              null,
              {
                formControl: form.get('settlementDate'),
                setFormControl: true,
                controlName: 'settlementDate',
                useDate: true
              },
              form.get('basket').value[0].cross
            );
          }
          break;
        case 'forward':
        case 'accumulator':
        case 'ndf':
        case 'spot':
          this.legDateService.calculateExpirationSettlementDate(
            null,
            {
              formControl: form.get('settlementDate'),
              setFormControl: true,
              controlName: 'settlementDate',
              useDate: true
            },
            form.get('cross').value
          ); // we want to only set the settlementDate
          break;
      }
      DateUtilities.onDateChanged(form.get('settlementDate'));
    }
  }

  public onDepositDateChange(val: string): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    removeDateLag('depositDate', leg); // attempt to remove (1m) if its there
    if (form.get('depositDate').valid && form.get('depositCurrency').value) {
      if (DateUtilities.isDateOfSlashFormat(val)) {
        val = DateUtilities.restructureDate(val);
        DateUtilities.onDateChanged(form.get('depositDate')); // needs to be done to look visually nice
      }
      const payload = {
        dateOrLag: val,
        currencyOrCalendar: form.get('depositCurrency').value
      };
      this.legDateService.nextBusinessDay(payload, 'depositDate', false, true); // calc the next businessDay
    }
  }

  public onPremiumPaymentDateChange(date: string): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    removeDateLag('premiumPaymentDate', leg); // attempt to remove (1m) if its there
    let crossOrCurrency = this.legHelperService.valueToUseAsCross(true);
    if (
      date &&
      (DateUtilities.isCorrectDateFormat(date) ||
        DateUtilities.isValidLag(date)) &&
      crossOrCurrency
    ) {
      const dateData = {
        formControl: form.get('premiumPaymentDate'),
        setFormControl: true,
        controlName: 'premiumPaymentDate',
        useDate: true // we are using this date
      };

      if (crossOrCurrency.length === 3) {
        this.legDateService.calculateExpirationSettlementDate(
          null,
          dateData,
          crossOrCurrency
        );
      } else {
        this.legDateService.calculateExpirationSettlementDate(
          dateData,
          null,
          crossOrCurrency
        );
      }
    }
  }

  public onValuationAdjustmentAmortizationStartDateChange(date: string): void {
    const form = this.legHelperService.form;
    DateUtilities.onDateChanged(
      form.get('valuationAdjustmentAmortizationStartDate')
    );

    const currentEndDate = form.get('valuationAdjustmentAmortizationEndDate')
      ?.value;

    if (
      !currentEndDate &&
      (DateUtilities.isDateOfSlashFormat(date) ||
        DateUtilities.isPrettyDate(date) ||
        DateUtilities.isDateOfDashedFormat(date))
    ) {
      let tempDate;
      if (DateUtilities.isDateOfSlashFormat(date)) {
        tempDate = DateUtilities.restructureDate(date);
      } else {
        tempDate = date;
      }

      const nextDay = new Date(new Date(tempDate).getTime() + 86400000);
      form
        .get('valuationAdjustmentAmortizationEndDate')
        ?.setValue(
          DateUtilities.dateToPrettyDate(
            DateUtilities.getDateInISOFormat(nextDay)
          )
        );
    }
  }

  public onValuationAdjustmentAmortizationEndDateChange(date: string): void {
    const form = this.legHelperService.form;
    if (DateUtilities.isPrettyDate(date)) {
      this.legHelperService.setDateOnDelay(
        // date is already in the right format just display it.
        'valuationAdjustmentAmortizationEndDate',
        form.get('valuationAdjustmentAmortizationEndDate').value
      );
    } else {
      DateUtilities.onDateChanged(
        form.get('valuationAdjustmentAmortizationEndDate')
      );
    }
  }

  public async handleProductExpiryChange(): Promise<void> {
    const leg = this.legHelperService.leg;
    switch (leg.productType) {
      case 'newdcd':
        await this.handleDcdExpiryDate();
        break;
      case 'eq.vanilla':
        // for future dev work call calculateExpirationSettlementDateCurrencyOnly()
        break;
      default:
        this.handleDefaultProductExpiryDate();
        break;
    }
  }

  private handleDefaultProductExpiryDate(): void {
    const form = this.legHelperService.form;
    if (form.get('cross')?.value) {
      form.get('settlementDate').patchValue('', { emitEvent: false });
      this.legDateService.calculateExpirationSettlementDate(
        {
          formControl: form.get('expiryDate'),
          setFormControl: true,
          controlName: 'expiryDate',
          useDate: true // we are using this date
        },
        {
          formControl: form.get('settlementDate'),
          setFormControl: true,
          controlName: 'settlementDate'
        },
        form.get('cross').value
      ); // set the expiryDate and also set the settlement date
    }

    if (form.get('basket')?.value[0]?.cross) {
      this.legDateService.calculateExpirationSettlementDate(
        {
          formControl: form.get('expiryDate'),
          setFormControl: true,
          controlName: 'expiryDate',
          useDate: true
        },
        {
          formControl: form.get('settlementDate'),
          setFormControl: true,
          controlName: 'settlementDate'
        },
        form.get('basket').value[0].cross
      );
    }
  }

  private async handleDcdExpiryDate(): Promise<void> {
    const form = this.legHelperService.form;
    if (form.get('expiryDate').valid) {
      // we want to get the dates and treasury rate when product is newdcd
      await this.legDateService.getDcdExpirationDateAndTreasuryRate(
        {
          formControl: form.get('redemptionDate'),
          setFormControl: true,
          controlName: 'redemptionDate',
          useDate: false
        },
        {
          formControl: form.get('expiryDate'),
          setFormControl: true,
          controlName: 'expiryDate',
          useDate: true
        }
      );
    }
  }
}
